import { inject, Injectable, Type } from '@angular/core';
import {
    MatDialog,
    MatDialogConfig,
    MatDialogRef,
} from '@angular/material/dialog';
import { SharedDialogOpenOptions } from '../models/shared-dialog-open-options';
import { SHARED_DIALOG_DEFAULT_OPTIONS } from './dialog-default-options';
import { mapSharedDialogOptionsToMatConfig } from './dialog-config-mapper';

/**
 * Application entry point for opening Angular Material dialogs.
 *
 * Merges {@link SHARED_DIALOG_DEFAULT_OPTIONS} with per-call options, maps them to
 * {@link MatDialogConfig} (see {@link mapSharedDialogOptionsToMatConfig}), and delegates
 * to {@link MatDialog.open}. Use this instead of injecting `MatDialog` directly when you
 * want shared defaults and typed dialog width ({@link MatDialogWidth}).
 */
@Injectable({
    providedIn: 'root',
})
export class SharedDialogService {
    private readonly matDialog = inject(MatDialog);

    /**
     * Opens a dialog component with merged defaults and mapped Material config.
     *
     * @param component - Component type to render inside the dialog.
     * @param options - Dialog options; `width` uses {@link MatDialogWidth}. Pass
     *   `matConfigOverride` to forward any other `MatDialogConfig` fields.
     */
    public open<TComponent, TData = unknown, TResult = unknown>(
        component: Type<TComponent>,
        options: SharedDialogOpenOptions<TData> = {}
    ): MatDialogRef<TComponent, TResult> {
        const mergedOptions: SharedDialogOpenOptions<TData> = {
            ...(SHARED_DIALOG_DEFAULT_OPTIONS as SharedDialogOpenOptions<TData>),
            ...options,
        };

        const config = mapSharedDialogOptionsToMatConfig(
            mergedOptions
        ) as MatDialogConfig<TData>;

        return this.matDialog.open<TComponent, TData, TResult>(
            component,
            config
        );
    }
}
